<template>
	<div>
		<!-- 遮罩层 -->
		<div v-if="loading" class="loading">
			<a-spin size="large" :spinning="loading"></a-spin>
		</div>
		<!-- PC -->
		<report-pc
			v-if="isPc && !loading"
			:isDelete="isDelete"
			:visibleShare="visibleShare"
			:shareKey="shareKey"
			:shareData="shareData"
			:electricityOverviewOptions="electricityOverviewOptions"
			:typicalLoadOptions="typicalLoadOptions"
			:dailyIncomeOptions="dailyIncomeOptions"
			:unitCapacityOptions="unitCapacityOptions"
			:averageDatas="averageDatas"
			:v1ReportInfos="v1ReportInfos"
			:schemeMethodsInfo="schemeMethodsInfo"
			:economicInfo="economicInfo"
			:strategyCurveListData="strategyCurveListData"
		></report-pc>
		<!-- H5 -->
		<report-mobile
			v-if="!isPc && !loading"
			:isDelete="isDelete"
			:shareData="shareData"
			:electricityOverviewOptions="electricityOverviewOptions"
			:typicalLoadOptions="typicalLoadOptions"
			:dailyIncomeOptions="dailyIncomeOptions"
			:unitCapacityOptions="unitCapacityOptions"
			:v1ReportInfos="v1ReportInfos"
			:schemeMethodsInfo="schemeMethodsInfo"
			:economicInfo="economicInfo"
			:averageDatas="averageDatas"
			:strategyCurveListData="strategyCurveListData"
		></report-mobile>
	</div>
</template>

<script setup>
import * as echarts from "echarts";

import {onMounted, ref, watch} from "vue";
// 引入route
import {useRoute, useRouter} from "vue-router";
const route = useRoute();
// components
import ReportPc from "./report-pc.vue";
import ReportMobile from "./report-mobile.vue";
// 引入setNotOpt
import {setNotOpt, defaultOptions, toFixed} from "@/plugins/index.js";

// 引入api
import {getReportData} from "@/api/scheme.js";
// 引入message
import {message} from "ant-design-vue";

// 加个遮罩层
let loading = ref(true);
// 判断加载pc还是移动
let isPc = ref(true);

// 记录shareKey
const shareKey = ref(null);

// 记录项目是否被删除
const isDelete = ref(false);

let map = {
	sharp: "尖",
	peak: "峰",
	flat: "平",
	valley: "谷",
};

let elecMap = {
	1: "充电",
	0: "静置",
	"-1": "放电",
};

const visibleShare = ref(true);

// 记录数据
const shareData = ref({});

// 配储分析参数
const strategyCurveListData = ref(null);

// 电量数据概览
const electricityOverviewOptions = ref({});

// 典型日负荷
const typicalLoadOptions = ref({});

// 日均收益
const dailyIncomeOptions = ref({});

// 单位容量日均收益
const unitCapacityOptions = ref({});

const averageDatas = ref({});

// 新增内容数据
const v1ReportInfos = ref(null);

// 配储方案数据
const schemeMethodsInfo = ref(null);

// 经济分析数据
const economicInfo = ref(null);

// 获取报告页数据详情
const getReportDetail = () => {
	getReportData(shareKey.value)
		.then(res => {
			if (res) {
				shareData.value = res;
				isDelete.value = false;
				const {
					dayElec,
					meanElec,
					typicalDayCurves,
					tfmCapacity,
					dayProfit,
					unitCapacityDayProfit,
					sharp,
					peak,
					flat,
					valley,
					creator,

					// 新增内容
					typicalDates,
					typicalDays,
					jobStepVO,
					power,
					capacity,
					strategyType,
					strategyInfo,
					economicAnalysisVO,
					economicAnalysisResultVO,

					// 配储分析参数列表
					strategyCurveList,
				} = res;
				shareData.value["electricPrice"] = [sharp, peak, flat, valley].join(" - ");

				// 是否为创建人
				if (creator) {
					visibleShare.value = true;
				} else {
					visibleShare.value = false;
				}

				strategyCurveListData.value = strategyCurveList;

				v1ReportInfos.value = {
					typicalDates,
					typicalDays,
					jobStepVO,
				};

				// 配储方案数据
				schemeMethodsInfo.value = {
					power,
					capacity,
					strategyType,
					strategyInfo,
				};
				// 经济分析数据
				economicInfo.value = {
					economicAnalysisVO,
					economicAnalysisResultVO,
				};
				console.log(economicInfo.value);

				// 绘制电量数据概览图
				electricOverview(dayElec, meanElec);
				// 绘制典型日负荷
				typicalDayCurvesDraw(typicalDayCurves, tfmCapacity);
				averageDatas.value = {
					dayProfit,
					unitCapacityDayProfit,
				};
				// 日均收益
				dailyIncomeOptions.value = processingData(dayProfit, "元/天", 2);
				// 单位容量日均收益
				let unitDayOpt = processingData(unitCapacityDayProfit, "元/(天·kWh)", 4);
				unitDayOpt.yAxis.nameTextStyle.padding = [0, 0, 5, 0];
				if (!isPc.value) {
					unitDayOpt.yAxis.nameTextStyle.padding = [0, -10, 5, 0];
				}
				unitCapacityOptions.value = unitDayOpt;
			} else {
				isDelete.value = true;
			}
		})
		.catch(err => {
			console.log(err);
		});
};

// 电量数据概览
const electricOverview = (dayElec = [], meanElec) => {
	let x_data = [],
		y_data = [];
	dayElec.map(el => {
		x_data.push(el.time);
		y_data.push(toFixed(el.value, 2));
	});
	let option = {
		...JSON.parse(JSON.stringify(defaultOptions)),
	};
	option.xAxis.data = x_data;
	option.series = [
		{
			name: "电量",
			data: y_data,
			type: "line",
			areaStyle: {
				color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
					{offset: 0, color: "#0082fc29"}, // 0% 处的颜色
					{offset: 1, color: "#0082fc00"}, // 100% 处的颜色
				]),
			},
		},
		{
			name: "日平均电量",
			data: Array(x_data.length).fill(toFixed(meanElec, 2)),
			type: "line",
			symbol: "none", // 去除圆点
		},
	];
	option.color = ["#0082FC", "#0BBA74"];
	option.yAxis.name = "电量/kWh";
	option.tooltip = {
		trigger: "axis",
		formatter: params => {
			const seriesData1 = params[0];
			// const seriesData2 = params[1];
			let str = "";
			if (params.length > 0) {
				str = `<span style='font-weight:600'>${seriesData1.name}</span>`;
			}
			params.map(param => {
				str += ` <br> ${param.marker} ${param.seriesName}：${param.data} kWh`;
			});

			console.log(str);
			return str;
		},
	};
	// 渲染图表
	electricityOverviewOptions.value = option;
};

// 典型日负荷
const typicalDayCurvesDraw = (typicalDayCurves, tfmCapacity) => {
	let x_data = [];
	let series = [];
	let colors = ["#0BBA74", "#0082FC", "#7C92BA"];
	typicalDayCurves.map((el, index) => {
		let obj = {
			name: `${el.name}`,
			data: [],
			type: "line",
			symbol: "none", // 去除圆点
		};
		el.curve.map(cl => {
			// 取第一项的时间作为x轴
			if (index === 0) {
				let arr = cl.time.split(":");
				arr.pop();
				x_data.push(arr.join(":"));
			}
			obj.data.push(toFixed(cl.value, 2));
		});
		series.push(obj);
	});
	let option = {
		...JSON.parse(JSON.stringify(defaultOptions)),
	};
	colors.push("#F30D0F");
	option.color = colors;
	option.xAxis.data = x_data;
	option.series = [
		...series,
		{
			name: "变压器容量",
			data: Array(x_data.length).fill(toFixed(tfmCapacity, 2)),
			type: "line",
			symbol: "none", // 去除圆点
		},
	];

	// 渲染图表
	option.yAxis.name = "功率/kW";
	option.tooltip = {
		trigger: "axis",
		formatter: params => {
			const seriesData1 = params[0];
			let str = `<span style='font-weight:600'>${seriesData1.name}</span>`;
			params.map(el => {
				str += ` <br> ${el.marker} ${el.seriesName}：${el.data} ${el.seriesName == "变压器容量" ? "kVA" : "kW"} `;
			});
			return str;
		},
	};
	if (!isPc.value) {
		option.grid = {
			left: "1%",
			right: "2%",
			top: "22%",
			bottom: "0%",
			containLabel: true,
		};
	}
	typicalLoadOptions.value = option;
};

// 处理收益数据
const processingData = (list = [], title = "", decimals = 2) => {
	let series = [
		{
			name: "智能策略",
			data: [],
			type: "bar",
			barWidth: isPc.value ? 16 : 6, // 设置柱状图的宽度
		},
		{
			name: "固定策略",
			data: [],
			type: "bar",
			barWidth: isPc.value ? 16 : 6, // 设置柱状图的宽度
		},
	];
	let resultOptions = {
		...JSON.parse(JSON.stringify(defaultOptions)),
	};

	let x_data = [];
	list.map(el => {
		x_data.push(el.capacity);
		series[0].data.push(toFixed(el.wise, decimals));
		series[1].data.push(toFixed(el.prev, decimals));
	});

	// 数据处理
	resultOptions.xAxis = {
		type: "category",
		name: "储能装机容量(kWh)",
		axisTick: {
			show: false,
		},
		data: x_data,
		nameTextStyle: {
			verticalAlign: "top",
			fontSize: 14,
			padding: [25, 0, 0, -136],
		},
	};
	resultOptions.series = series;
	resultOptions.grid.bottom = "10%";
	resultOptions.yAxis.name = title;
	resultOptions.yAxis.nameLocation = "end";
	resultOptions.color = ["#0082FC", "#0BBA74"];
	// resultOptions.xAxis.name = "储能装机容量(kWh)";
	resultOptions.tooltip = {
		trigger: "axis",
		formatter: params => {
			const seriesData1 = params[0];
			const seriesData2 = params[1];
			let str = ``;
			if (params.length > 0) {
				str = `<span style='font-weight:600'>储能装机容量：${seriesData1.name}kWh</span>`;
			}
			params.map(params => {
				str += `<br> ${params.marker}${params.seriesName}：${params.data} ${title} `;
			});
			return str;
		},
	};
	return resultOptions;
};

onMounted(() => {
	// 实时监听窗口宽度变化
	window.addEventListener("resize", function () {
		let screenWidth = window.screen.width;
		if (screenWidth < 768) {
			// 移动端逻辑
			isPc.value = false;
		} else {
			// PC端逻辑
			isPc.value = true;
		}
	});
	// 页面加载时判断初始窗口宽度
	window.addEventListener("load", function () {
		let screenWidth = window.screen.width;

		if (screenWidth < 768) {
			// 移动端逻辑
			isPc.value = false;
		} else {
			// PC端逻辑
			isPc.value = true;
		}

		loading.value = false;
	});

	// 根据shareKey查询详情;
	shareKey.value = route.query.shareKey;
	getReportDetail();
});
watch(isPc, newVal => {
	console.log(newVal);
	getReportDetail();
});
</script>

<style lang="less" scoped>
.loading {
	height: 100vh;
	display: flex;
	justify-content: center;
	align-items: center;
}
</style>
